import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { borderColorTertiary, backgroundColorLevelCurrent, textColorPrimary } from "@churchofjesuschrist/eden-style-constants";
import { useClickBoundary } from "@churchofjesuschrist/eden-click-boundary";
import Card from "@churchofjesuschrist/eden-card";
import { createPopper } from "@popperjs/core";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCallout = styled(Card).withConfig({
  componentId: "sc-yoesbz-0"
})(["--calloutBackgroundColor:", ";--calloutBorderColor:", ";display:block;color:", ";position:absolute;min-width:min-content;max-width:19rem;opacity:0;z-index:3;padding:0;margin:0;&:before{display:block;content:\"\";height:11px;top:-11px;width:100%;position:absolute;[data-popper-placement^=\"top\"]&{top:auto;bottom:-11px;}", "}&[data-popper-placement]{opacity:1;}"], backgroundColorLevelCurrent, borderColorTertiary, textColorPrimary, _ref => {
  let {
    arrow
  } = _ref;
  return arrow === "overlay" ? "pointer-events: none;" : "";
});
const Arrow = styled.div.withConfig({
  componentId: "sc-yoesbz-1"
})(["position:absolute;&:before,&:after{", " border:solid transparent;content:\" \";height:0;width:0;position:absolute;z-index:4;bottom:100%;left:50%;}[data-popper-placement] &{bottom:0;&:after{border-top-color:var(--calloutBackgroundColor);border-width:9px;margin-left:-8px;border-bottom-width:0;}&:before{border-top-color:var(--calloutBorderColor);border-width:11px;margin-left:-10px;border-bottom-width:0;}}[data-popper-placement^=\"top\"] &{&:before,&:after{top:100%;}}[data-popper-placement^=\"bottom\"] &{bottom:100%;&:after,&:before{transform:rotate(-180deg);bottom:0;top:auto;}}"], _ref2 => {
  let {
    arrow
  } = _ref2;
  return arrow === "overlay" ? "pointer-events: none;" : "";
});
const Callout = _ref3 => {
  let {
    forRef,
    open = false,
    arrow = "normal",
    onClickOutside,
    children,
    placement = "bottom",
    ...props
  } = _ref3;
  const arrowRef = useRef(null);
  const onClickOutsideInternal = useCallback(event => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(event);
    });
  }, [open, onClickOutside]);
  const callout = useClickBoundary({
    onClickInside: x => x,
    onClickOutside: onClickOutsideInternal
  });
  useEffect(() => {
    if (open) {
      const handleEsc = event => {
        if (event.code === "Escape") {
          onClickOutsideInternal(event);
        }
      };
      document.addEventListener("keyup", handleEsc, {
        capture: true
      });
      return () => document.removeEventListener("keyup", handleEsc, {
        capture: true
      });
    }
  }, [open, onClickOutsideInternal]);
  useEffect(() => {
    if (forRef.current && callout.current && open) {
      var _callout$current$show, _callout$current;
      (_callout$current$show = (_callout$current = callout.current).show) === null || _callout$current$show === void 0 ? void 0 : _callout$current$show.call(_callout$current);
      const popperInstance = createPopper(forRef.current, callout.current, arrow === "none" ? {
        placement
      } : {
        placement,
        modifiers: [{
          name: "arrow",
          options: {
            element: arrowRef.current
          }
        }, {
          name: "offset",
          options: {
            ...(arrow === "overlay" ? {
              offset: [0, 0]
            } : {
              offset: [0, 10]
            })
          }
        }]
      });
      return () => {
        popperInstance.destroy();
      };
    }
  }, [callout, forRef, open, arrow, placement]);
  return open ? /*#__PURE__*/_jsxs(StyledCallout, {
    renderAs: "dialog",
    depth: "overlaid",
    arrow: arrow,
    ...props,
    ref: callout,
    children: [arrow === "none" ? null : /*#__PURE__*/_jsx(Arrow, {
      arrow: arrow,
      ref: arrowRef
    }), children]
  }) : null;
};
Callout.propTypes = {
  /** The ref to which the callout belongs */
  forRef: PropTypes.object.isRequired,
  /** Content inside callout */
  children: PropTypes.node.isRequired,
  /** Whether the callout is open */
  open: PropTypes.bool,
  /** Callout arrow's appearance */
  arrow: PropTypes.oneOf(["none", "normal", "overlay"]),
  /** Default location of the callout relative to the `forRef`. Will flip if there is not enough available space in that direction. */
  placement: PropTypes.oneOf(["top", "bottom"]),
  /** Handler for when a click occurs outside of the callout (e.g., to close the callout) */
  onClickOutside: PropTypes.func
};
export default Callout;